import { Add, Delete, Edit, UploadFile } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { navigate } from '@reach/router';
import { useEffect, useState } from 'react';
import apiClient from '../api/client';
import AdvancedFilters from '../components/AdvancedFilters';
import ExcelImportDialog from '../components/ExcelImportDialog';
import Layout from '../components/Layout';
import SearchField from '../components/SearchField';
import StyledTableContainer from '../components/StyledTableContainer';
import { useAuth } from '../contexts/AuthContext';
import { useRealtimeSSE } from '../hooks/useRealtimeSSE';

const CategoriesPage = () => {
  const { user, loading: authLoading } = useAuth();

  // ✅ Подключение к SSE для real-time обновлений
  useRealtimeSSE();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [page, setPage] = useState(1);
  const perPage = 20;
  const [totalPages, setTotalPages] = useState(null);

  const filterFields = [
    { value: 'name', label: 'Название' },
    { value: 'description', label: 'Описание' },
  ];

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
    if (user) {
      fetchCategories();
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (categories.length > 0) {
      let filtered = categories;

      // Применяем поиск
      if (searchQuery) {
        filtered = filtered.filter(category => {
          return (
            category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (category.description && category.description.toLowerCase().includes(searchQuery.toLowerCase()))
          );
        });
      }

      // Применяем расширенные фильтры
      if (advancedFilters.length > 0) {
        filtered = filtered.filter(category => {
          return advancedFilters.every(filter => {
            const value = category[filter.field];
            if (value === null || value === undefined) return false;

            const stringValue = String(value).toLowerCase();
            const filterValue = filter.value.toLowerCase();

            switch (filter.operator) {
              case 'contains':
                return stringValue.includes(filterValue);
              case 'equals':
                return stringValue === filterValue;
              case 'startsWith':
                return stringValue.startsWith(filterValue);
              case 'endsWith':
                return stringValue.endsWith(filterValue);
              case 'greaterThan':
                return Number(value) > Number(filter.value);
              case 'lessThan':
                return Number(value) < Number(filter.value);
              default:
                return true;
            }
          });
        });
      }

      setFilteredCategories(filtered);
    }
  }, [searchQuery, categories, advancedFilters]);

  const fetchCategories = async (pageParam = page) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/categories?page=${pageParam}&per_page=${perPage}`);
      setCategories(response.data);
      const pagesMeta = response.pagination?.pages ?? parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages']);
      if (!isNaN(pagesMeta)) setTotalPages(pagesMeta);
      setError('');
    } catch (err) {
      console.error('Ошибка при загрузке категорий:', err);
      setError('Не удалось загрузить список категорий');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || ''
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await apiClient.put(`/categories/${editingCategory.id}`, formData);
      } else {
        await apiClient.post('/categories', formData);
      }
      handleCloseDialog();
      fetchCategories();
    } catch (err) {
      console.error('Ошибка при сохранении категории:', err);
      setError('Не удалось сохранить данные категории');
    }
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('Вы уверены, что хотите удалить эту категорию? Все связанные продукты также будут удалены!')) {
      try {
        await apiClient.delete(`/categories/${categoryId}`);
        fetchCategories();
      } catch (err) {
        console.error('Ошибка при удалении категории:', err);
        setError('Не удалось удалить категорию');
      }
    }
  };

  const handleApplyFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const handleResetFilters = () => {
    setAdvancedFilters([]);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    fetchCategories(value);
  };

  return (
    <Layout>
      <Box sx={{
        p: 3,
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
        width: '100%',
        boxSizing: 'border-box',
      }}>
        <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: { sm: 'space-between' }, gap: { xs: 2, sm: 0 } }}>
          <Typography variant="h4" component="h1" sx={{ mr: { sm: 'auto' } }}>
            Категории
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' }, gap: { xs: 1, sm: 2 } }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Добавить категорию
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFile />}
              onClick={() => setImportOpen(true)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Импорт из Excel
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск категорий..."
            sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
          />
          <AdvancedFilters
            filters={advancedFilters}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            fields={filterFields}
            sx={{ width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}
          />
        </Box>

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
            {error}
          </Paper>
        )}
        <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'white' }}>Название</TableCell>
                <TableCell sx={{ color: 'white' }}>Описание</TableCell>
                <TableCell sx={{ color: 'white', position: 'sticky', right: 0, background: 'rgba(30,30,30,0.8)', zIndex: 2 }}>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell sx={{ color: 'white' }} colSpan={3} align="center">Загрузка...</TableCell>
                </TableRow>
              ) : filteredCategories.length === 0 ? (
                <TableRow>
                  <TableCell sx={{ color: 'white' }} colSpan={3} align="center">
                    {searchQuery ? 'Категории не найдены' : 'Категории не найдены'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredCategories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell sx={{ color: 'white' }}>{category.name}</TableCell>
                    <TableCell sx={{ color: 'white' }}>{category.description || '—'}</TableCell>
                    <TableCell sx={{ position: 'sticky', right: 0, background: 'rgba(30,30,30,0.7)', zIndex: 1 }}>
                      <IconButton onClick={() => handleOpenDialog(category)}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(category.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
        {totalPages && totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination color="primary" count={totalPages} page={page} onChange={handlePageChange} />
          </Box>
        )}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editingCategory ? 'Редактировать категорию' : 'Добавить категорию'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Название"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Описание"
              name="description"
              value={formData.description}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button type="submit" variant="contained">Сохранить</Button>
          </DialogActions>
        </form>
      </Dialog>
      <ExcelImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        endpoint="/categories/import"
        onSuccess={fetchCategories}
      />
    </Layout>
  );
};

export default CategoriesPage; 